import React, { useState, useEffect } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Send, CheckCircle, XCircle, Clock, Trash2, Plus } from "lucide-react";

export default function ExtensionPostQueue() {
  const [queue, setQueue] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlatform, setSelectedPlatform] = useState("onlyfans_vip");
  const [showHistory, setShowHistory] = useState(false);
  
  // New post form
  const [newPost, setNewPost] = useState({
    platform: "onlyfans_vip",
    text: "",
    price: null,
    approved: false,
  });

  const platforms = [
    { value: "onlyfans_vip", label: "OnlyFans VIP" },
    { value: "onlyfans_free", label: "OnlyFans Free" },
    { value: "justforfans", label: "JustForFans" },
  ];

  useEffect(() => {
    loadQueue();
    loadHistory();
  }, [selectedPlatform]);

  const loadQueue = async () => {
    try {
      const res = await api.get(`/api/extension/post-queue?platform=${selectedPlatform}`);
      setQueue(res.data.posts || []);
    } catch (e) {
      toast.error("Failed to load post queue");
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const res = await api.get(`/api/extension/post-queue/history?platform=${selectedPlatform}&limit=20`);
      setHistory(res.data.posts || []);
    } catch (e) {
      console.error("Failed to load history:", e);
    }
  };

  const createPost = async (e) => {
    e.preventDefault();
    
    if (!newPost.text.trim()) {
      toast.error("Post text is required");
      return;
    }

    try {
      await api.post("/api/extension/post-queue", newPost);
      toast.success("Post added to queue");
      setNewPost({ platform: selectedPlatform, text: "", price: null, approved: false });
      loadQueue();
    } catch (e) {
      toast.error("Failed to create post");
    }
  };

  const approvePost = async (postId) => {
    try {
      await api.post(`/api/extension/post-queue/${postId}/approve`);
      toast.success("Post approved - ready for extension");
      loadQueue();
    } catch (e) {
      toast.error("Failed to approve post");
    }
  };

  const deletePost = async (postId) => {
    if (!confirm("Delete this post from queue?")) return;

    try {
      await api.delete(`/api/extension/post-queue/${postId}`);
      toast.success("Post deleted");
      loadQueue();
    } catch (e) {
      toast.error("Failed to delete post");
    }
  };

  return (
    <AppLayout
      title="Extension Post Queue"
      overline="CHROME EXTENSION"
      actions={
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="btn-ghost"
        >
          {showHistory ? "Show Queue" : "Show History"}
        </button>
      }
    >
      <div className="max-w-5xl space-y-6">
        {/* Info Banner */}
        <div className="rounded-lg border p-4" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
          <div className="flex items-start gap-3">
            <Send size={20} style={{ color: "var(--gio-primary)", flexShrink: 0 }} />
            <div>
              <h3 className="font-bold text-sm mb-1" style={{ color: "var(--gio-text)" }}>
                Extension v0.5.0 - Post Queue Prefill
              </h3>
              <p className="text-xs leading-relaxed" style={{ color: "var(--gio-muted-2)" }}>
                Create and approve posts here. The Chrome extension will prefill them on OnlyFans/JustForFans. 
                <strong> You must manually click Post</strong> on the platform. Extension tracks when posted.
              </p>
            </div>
          </div>
        </div>

        {/* Platform Selector */}
        <div className="flex gap-2">
          {platforms.map((platform) => (
            <button
              key={platform.value}
              onClick={() => setSelectedPlatform(platform.value)}
              className={`px-4 py-2 rounded text-sm font-bold transition ${
                selectedPlatform === platform.value
                  ? "bg-purple-600 text-white"
                  : "bg-gray-800 text-gray-300 hover:bg-gray-700"
              }`}
            >
              {platform.label}
            </button>
          ))}
        </div>

        {!showHistory ? (
          <>
            {/* Create New Post Form */}
            <form onSubmit={createPost} className="rounded-lg border p-6 space-y-4" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
              <h3 className="font-bold" style={{ color: "var(--gio-text)" }}>
                <Plus size={16} className="inline mr-2" />
                Create New Post
              </h3>

              <div>
                <label className="block text-sm mb-2" style={{ color: "var(--gio-text)" }}>
                  Post Text
                </label>
                <textarea
                  value={newPost.text}
                  onChange={(e) => setNewPost({ ...newPost, text: e.target.value })}
                  placeholder="Enter post content..."
                  className="w-full rounded border px-3 py-2 text-sm"
                  style={{ borderColor: "var(--gio-border)", background: "var(--gio-bg)", color: "var(--gio-text)" }}
                  rows={4}
                  maxLength={5000}
                  required
                />
                <p className="text-xs mt-1" style={{ color: "var(--gio-muted-3)" }}>
                  {newPost.text.length} / 5000 characters
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-2" style={{ color: "var(--gio-text)" }}>
                    Platform
                  </label>
                  <select
                    value={newPost.platform}
                    onChange={(e) => setNewPost({ ...newPost, platform: e.target.value })}
                    className="w-full rounded border px-3 py-2 text-sm"
                    style={{ borderColor: "var(--gio-border)", background: "var(--gio-bg)", color: "var(--gio-text)" }}
                  >
                    {platforms.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm mb-2" style={{ color: "var(--gio-text)" }}>
                    Price (optional)
                  </label>
                  <input
                    type="number"
                    value={newPost.price || ""}
                    onChange={(e) => setNewPost({ ...newPost, price: e.target.value ? parseFloat(e.target.value) : null })}
                    placeholder="Leave empty for free"
                    className="w-full rounded border px-3 py-2 text-sm"
                    style={{ borderColor: "var(--gio-border)", background: "var(--gio-bg)", color: "var(--gio-text)" }}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="auto-approve"
                  checked={newPost.approved}
                  onChange={(e) => setNewPost({ ...newPost, approved: e.target.checked })}
                  className="rounded"
                />
                <label htmlFor="auto-approve" className="text-sm" style={{ color: "var(--gio-text)" }}>
                  Approve immediately (ready for extension)
                </label>
              </div>

              <button type="submit" className="btn-primary">
                <Plus size={16} className="inline mr-2" />
                Add to Queue
              </button>
            </form>

            {/* Queue Items */}
            <div className="space-y-3">
              <h3 className="font-bold text-lg" style={{ color: "var(--gio-text)" }}>
                Queue ({queue.length} items)
              </h3>

              {loading ? (
                <p style={{ color: "var(--gio-muted-2)" }}>Loading...</p>
              ) : queue.length === 0 ? (
                <div className="rounded-lg border p-6 text-center" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
                  <p style={{ color: "var(--gio-muted-2)" }}>No posts in queue for {platforms.find(p => p.value === selectedPlatform)?.label}</p>
                </div>
              ) : (
                queue.map((post) => (
                  <div
                    key={post.id}
                    className="rounded-lg border p-4 space-y-3"
                    style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {post.approved ? (
                            <CheckCircle size={16} className="text-green-500" />
                          ) : (
                            <Clock size={16} className="text-yellow-500" />
                          )}
                          <span className="text-xs font-bold" style={{ color: "var(--gio-muted-2)" }}>
                            {post.approved ? "APPROVED - Ready for Extension" : "PENDING APPROVAL"}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap" style={{ color: "var(--gio-text)" }}>
                          {post.text}
                        </p>
                        {post.price && (
                          <p className="text-xs mt-2" style={{ color: "var(--gio-primary)" }}>
                            Price: ${post.price.toFixed(2)}
                          </p>
                        )}
                      </div>

                      <div className="flex gap-2">
                        {!post.approved && (
                          <button
                            onClick={() => approvePost(post.id)}
                            className="btn-primary text-sm px-3 py-1"
                          >
                            <CheckCircle size={14} className="inline mr-1" />
                            Approve
                          </button>
                        )}
                        <button
                          onClick={() => deletePost(post.id)}
                          className="btn-ghost text-sm px-3 py-1"
                        >
                          <Trash2 size={14} className="inline mr-1" />
                          Delete
                        </button>
                      </div>
                    </div>

                    <div className="text-xs pt-2 border-t" style={{ borderColor: "var(--gio-border)", color: "var(--gio-muted-3)" }}>
                      Created: {new Date(post.created_at).toLocaleString()}
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        ) : (
          /* History View */
          <div className="space-y-3">
            <h3 className="font-bold text-lg" style={{ color: "var(--gio-text)" }}>
              Posted History ({history.length} items)
            </h3>

            {history.length === 0 ? (
              <div className="rounded-lg border p-6 text-center" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
                <p style={{ color: "var(--gio-muted-2)" }}>No posted items yet</p>
              </div>
            ) : (
              history.map((post) => (
                <div
                  key={post.id}
                  className="rounded-lg border p-4 opacity-75"
                  style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}
                >
                  <div className="flex items-start gap-2 mb-2">
                    <CheckCircle size={16} className="text-green-500" />
                    <span className="text-xs font-bold text-green-500">POSTED</span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap" style={{ color: "var(--gio-text)" }}>
                    {post.text.substring(0, 200)}{post.text.length > 200 ? "..." : ""}
                  </p>
                  <div className="text-xs mt-2" style={{ color: "var(--gio-muted-3)" }}>
                    Posted: {post.posted_at ? new Date(post.posted_at).toLocaleString() : "Unknown"}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
